/**
 * ThemeContext
 * Single Aurora theme - no user selection needed
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';

interface ThemeContextValue {
  theme: 'aurora';
  resolvedTheme: 'aurora';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * ThemeProvider
 * Applies the Aurora theme to the entire application
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Apply aurora theme class to document on mount
  useEffect(() => {
    const root = document.documentElement;

    // Remove any old theme classes
    root.classList.remove('light', 'system');

    // Add aurora theme class + dark for Tailwind dark: prefixes
    root.classList.add('aurora', 'dark');

    // Set data attribute for CSS selectors
    root.setAttribute('data-theme', 'aurora');
  }, []);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme: 'aurora',
      resolvedTheme: 'aurora',
    }),
    []
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to use theme context
 * Throws error if used outside provider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};
